import * as React from 'react';
import { cn } from '../utils/cn';

export interface AvatarProps {
  /** Image source URL */
  src?: string;
  /** Alt text */
  alt?: string;
  /** Avatar size */
  size?: 'large' | 'default' | 'small' | number;
  /** Shape */
  shape?: 'circle' | 'square';
  /** Icon element (fallback when no src/children) */
  icon?: React.ReactNode;
  /** Additional class names */
  className?: string;
  /** Inline style */
  style?: React.CSSProperties;
  /** Text initials (children) */
  children?: React.ReactNode;
  /** Error handler when image fails to load */
  onError?: () => any;
}

const sizeMap: Record<string, string> = {
  large: 'h-10 w-10 text-base',
  default: 'h-8 w-8 text-sm',
  small: 'h-6 w-6 text-xs',
};

export function Avatar({
  src,
  alt = '',
  size = 'default',
  shape = 'circle',
  icon,
  className,
  style,
  children,
}: AvatarProps) {
  const [imgError, setImgError] = React.useState(false);

  const sizeClass = typeof size === 'string' ? sizeMap[size] : '';
  const sizeStyle: React.CSSProperties =
    typeof size === 'number' ? { width: size, height: size, fontSize: size * 0.4 } : {};

  const shapeClass = shape === 'circle' ? 'rounded-full' : 'rounded-sm';

  // Show image
  if (src && !imgError) {
    return (
      <img
        src={src}
        alt={alt}
        onError={() => setImgError(true)}
        className={cn(
          'inline-block object-cover bg-gray-200',
          shapeClass,
          sizeClass,
          '!filter-none', // Override global desaturate for avatars
          className
        )}
        style={{ ...sizeStyle, ...style }}
      />
    );
  }

  // Show initials or icon fallback
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center bg-accent/20 text-accent font-body font-medium',
        shapeClass,
        sizeClass,
        className
      )}
      style={{ ...sizeStyle, ...style }}
    >
      {children || icon || '?'}
    </span>
  );
}
